import React from 'react'
import { Card, Divider, Flex, InfoButton, Text, Tooltip } from '@indeed/ifl-components'
import { Information } from '@indeed/ifl-icons'

import { currentIntl } from 'syft-acp-util/intl'
import { getSafeCurrencySymbol } from 'syft-acp-util/formatting'
import type {
  ConfiguredUpliftEntity,
  ConfiguredUpliftWithAmount,
} from 'syft-acp-core/store/configuredUplift/types'

/**
 * What the amount means, which depends on where it is held. The figure alone is ambiguous: an
 * amount set on a venue and one inherited from its client read identically.
 */
export const upliftExplanation = (uplift: ConfiguredUpliftEntity) => {
  switch (uplift.source) {
    case 'venue_exception':
      return 'This is the uplift amount configured specifically for this venue.'
    case 'client_exception':
      return (
        'This is the uplift amount configured for this client and applies to all venues ' +
        'unless a venue has its own amount.'
      )
    default:
      return ''
  }
}

export const TOOLTIP =
  'Set in Inspector by Strategy or Engineering. It is added to the client pay rate when an ' +
  'automatic uplift runs for a shift at this scope.'

export const formatHourly = (amount: number) =>
  `${getSafeCurrencySymbol(currentIntl.currencyCode)}${amount.toFixed(2)} / hr`

/**
 * Read-only. Inspector stays the only place an amount is set, so this renders as a panel rather
 * than a disabled control, which would still look like something a reader could change.
 */
export const ConfiguredUpliftCard = ({ uplift }: { uplift: ConfiguredUpliftWithAmount }) => (
  <Card sx={{ mt: 3, ml: 4, maxWidth: 'fit-content' }}>
    <Flex sx={{ alignItems: 'stretch' }}>
      <Flex sx={{ flexDirection: 'column', justifyContent: 'center', gap: 1, px: 4, py: 3 }}>
        <Text level={2} sx={{ whiteSpace: 'nowrap' }}>
          Automatic uplift
        </Text>
        <Text level={4} sx={{ fontWeight: 'bold' }}>
          {formatHourly(uplift.amount)}
        </Text>
      </Flex>
      <Divider vertical />
      <Flex sx={{ alignItems: 'center', gap: 3, px: 4, py: 3 }}>
        <Text level={2}>{upliftExplanation(uplift)}</Text>
        <Tooltip delay={400} label={TOOLTIP}>
          <InfoButton variant="icon" aria-label="How this uplift is set">
            <Information aria-hidden size="sm" color="blue" />
          </InfoButton>
        </Tooltip>
      </Flex>
    </Flex>
  </Card>
)
